import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import cx from 'classnames';
import { useDispatch } from 'react-redux';
import styled from 'styled-components/macro';
import { destructUUID } from '@tovia/man-app-utils/lib/helpers/uuids';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart as favoriteIcon } from '@fortawesome/pro-regular-svg-icons';
import { faHeart as unfavoriteIcon } from '@fortawesome/pro-solid-svg-icons';
import { faComments } from '@fortawesome/pro-light-svg-icons';
import { Badge, Badges, RelatedMovies, TABLET_LANDSCAPE } from '@tovia/man-ui';
// The destructured import from man-ui is the wrong component.
import RatingSlider from '@tovia/man-ui/lib/components/RatingSlider/RatingSlider';
import { addFavor as addFavorFn, removeFavor as removeFavorFn } from 'src/client/redux/modules/favoriteInfo';
import { load as loadFn, reset as resetMovieFn } from 'src/client/redux/modules/movie';
import { rateItem as rateItemFn } from 'src/client/redux/modules/ratingInfo';
import { reset as resetPermissionsFn } from 'src/client/redux/modules/galleryPermissions';
import LoaderWrapper from 'src/client/components/Loader/LoaderWrapper';
import { CommentSection } from '../CommentSection/CommentSection';
import { ApiClient, scrollTo } from 'src/client/helpers';
import { FULL_MODE, PPS_PURCHASED, REGULAR_GUEST_MODE, SS_GUEST_MODE } from 'src/client/helpers/constants';
import { ActivityPanel } from '../Panels/ActivityPanel';
import RecentCookiesItemInjector from '../RecentCookies/RecentCookiesItemInjector';
import { UpgradeDialog } from 'src/client/components/UpgradeDialog/UpgradeDialog';
import { Panel as MovieInformationPlaceholder } from 'src/client/components/Placeholders/Panels/Panel';
import { useSelector, useSettingsSelector } from 'src/client/redux/modules/helpers/useSelector';
import urls from 'src/shared/urls';
import { Site } from 'src/client/redux/modules/site';
import MovieActions from './MovieActions';
import { RelatedGalleries } from './RelatedGalleries';
import { MovieDetails } from './MovieDetails';
import { JoinButton, redirectToJoin, useJoinUrlGenerator } from 'src/client/components/buttons/JoinButton';
import useAccessToken from '../../helpers/useAccessToken';
import { PartnerInlineBanner } from 'src/client/components/PartnerBanners/PartnerInlineBanner';
import { PromoBanner } from 'src/client/containers/PopupBanners/PromoBanner';
import { PromoInlineBanner } from 'src/client/containers/Promo/PromoInlineBanner';
import { PromoCrossBanner } from 'src/client/containers/Promo/PromoCrossBanner';
import { usePromotions } from 'src/client/helpers/usePromotions';
import { NetworkGalleriesSlider } from 'src/client/components/NetworkGalleriesSlider';
import { useFeatureFlags, useShowSfwSite } from 'src/client/helpers/useFeatureFlags';
import { BlurrableImage } from 'src/client/components/ui/BlurredImage';
import ReCAPTCHA from 'react-google-recaptcha';
import { MovieCams } from './MovieCams';
import { AccessMode, getMovieCover, Videos } from './movieUtils';
import { MovieHelmet } from './MovieHelmet';
import { MoviePlayerContainer } from './MoviePlayerContainer';

const loadVideoUrls = async ({ accessMode, item, allowedStreamIds, contentCdnUrl, token, reCaptchaTokenRef }) => {
  const mp4: string[] = [];
  if (accessMode === FULL_MODE || accessMode === PPS_PURCHASED) {
    const viewableMovieFiles =
      item && item.files && item.files.sizes
        ? item.files.sizes.videos.filter((video) => allowedStreamIds.includes(video.id)).map((video) => video.id)
        : [];

    const videoSizes = viewableMovieFiles.filter((id) => id === '720p' || id === '1080p');

    if (videoSizes.length === 0 && viewableMovieFiles.length > 0) {
      videoSizes.push(viewableMovieFiles[0]);
    }

    const api = new ApiClient();

    const promises: Promise<{
      body: {
        url: string;
      };
    }>[] = [];
    for await (const video of videoSizes) {
      const recaptchaToken = await reCaptchaTokenRef?.executeAsync();
      promises.push(
        api.get(
          `/api/download-media/${item.UUID}/film/${video}.mp4?link=1${token ? `&token=${token}` : ''}${
            recaptchaToken ? `&recaptchaToken=${recaptchaToken}` : ''
          }`,
        ),
      );
    }

    const responses = await Promise.all(promises);

    responses.forEach((mp4res) => {
      if (mp4res?.body?.url) {
        mp4.push(mp4res.body.url);
      }
    });
  } else if (item && item.files && item.files.teasers.includes('mp4')) {
    if (accessMode === REGULAR_GUEST_MODE) {
      mp4.push(`${contentCdnUrl}/${item.siteUUID}/media/${item.UUID}/tease_${item.UUID}.mp4`);
    }
  }

  return mp4;
};

const eventTrigger = ({ name }) => {
  window.dataLayer.push({
    event: 'select_content',
    content_type: 'movie',
    item_id: `${name}`,
  });
};

export const Movie = () => {
  const { allowedStreamIds, loaded: loadedStreamIds } = useSelector((state) => state.galleryPermissions);
  const { error, globalContent, isLightsOn, item, loading, loaded } = useSelector((state) => state.movie);
  const { pixelApiUrl, siteSubdomain } = useSelector((state) => state.app);
  const { user } = useSelector((state) => state.auth);
  const sites = useSelector((state) => state.sites.items);
  const parentSite = useSelector((state) => state.site);
  const ratingInfo = useSelector((state) => state.ratingInfo);
  const favoriteInfo = useSelector((state) => state.favoriteInfo);
  const { comments } = useSelector((state) => state.comments);
  const { config, defaultCdnUrl } = useSelector((state) => state.app);
  const ratingsScale = useSettingsSelector<number>('ratingsScale');
  const showSfwSite = useShowSfwSite();

  const [reCaptchaElement, setReCaptchaTokenElement] = useState(null);
  const { enableReCaptchaDownload } = useFeatureFlags();
  const reCaptchaRef = useRef<ReCAPTCHA>(null);

  const getReCaptchaRef = async (reCaptchaRef) => {
    const reCaptchaTokenRef = await reCaptchaRef?.current;
    setReCaptchaTokenElement(reCaptchaTokenRef);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (reCaptchaRef.current) {
        getReCaptchaRef(reCaptchaRef);
        clearInterval(interval);
      }
    }, 100);
    return () => clearInterval(interval);
  }, []);

  const needsCdnUrl = item?.isStaffSelection || item?.isIntimateSelection;

  const { contentCdnUrl: appContentCdn } = useSelector((state) => state.app);
  const contentCdnUrl = useMemo(() => (needsCdnUrl ? defaultCdnUrl : appContentCdn), [
    defaultCdnUrl,
    appContentCdn,
    needsCdnUrl,
  ]);

  const { favoritingObjectUUIDs: favorites } = favoriteInfo;
  const { ratings } = ratingInfo;

  const [accessMode, setAccessMode] = useState<AccessMode>(null);
  const [member, setMember] = useState(false);

  const [selfFavorite, setSelfFavorite] = useState<false | true>(false);
  const [videos, setVideos] = useState<Videos>({ loaded: false, loading: false, videos: [] });
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [site, setSite] = useState<null | Site>();
  const generateJoinUrl = useJoinUrlGenerator();
  const { isPromoInlineBannerActive } = usePromotions();

  const [showPromoBanner, setShowPromoBanner] = useState(false);
  const [isPromoCrossBannerActive, setPromoCrossBannerActive] = useState(false);

  useEffect(() => {
    if (user && item && item.isPromoGallery && parentSite && item.UUID !== parentSite.UUID) {
      const isSubscribedToPromoGallerySite = user?.subscriptions?.some(
        (subscription) => destructUUID(subscription.siteUUID) === item.siteUUID,
      );

      setShowPromoBanner(!isSubscribedToPromoGallerySite);
      setPromoCrossBannerActive(!isSubscribedToPromoGallerySite);
    }
  }, [item, parentSite, user]);

  const dispatch = useDispatch();

  const params = useParams<{ movieName: string; date: string }>();
  const history = useHistory();
  const { accessToken: token } = useAccessToken();

  useEffect(() => {
    if (error?.status === 404) {
      history.replace(urls.get.notFound);
    }
  }, [error, history]);

  const loadData = useCallback(
    ({ movieName, date, token }) => {
      const reqParams: {
        name: string;
        date: string;
        token?: string;
      } = {
        name: movieName,
        date,
      };

      if (token) {
        reqParams.token = token;
      }

      dispatch(loadFn(reqParams));
    },
    [dispatch],
  );

  useEffect(() => {
    loadData({
      movieName: params.movieName,
      date: params.date,
      token,
    });

    return () => {
      dispatch(resetMovieFn());
      dispatch(resetPermissionsFn());
    };
  }, [dispatch, loadData, params.movieName, params.date, token]);

  useEffect(() => {
    const purchased = item && (item.hasPermissions || allowedStreamIds.length > 0);
    const isStaffSelection = item?.isStaffSelection;
    const memberStatus = !!user && user.validSubscription;

    /** ***************************************************
     CASES (default - regular guest):
     ------
     1. REGULAR_GUEST_MODE = guest && !staffSelection : regular guest mode
     2. FULL_MODE = member && (!staffSelection || purchased) : user has full access,
     no ads
     3. SS_GUEST_MODE = staffSelection && !purchased : unpurchased staff selection movie
     4. PPS_PURCHASED = guest && purchased : full content with ads

     NOTE: MEMBERS WITH EXPIRED SUBSCRIPTION CONSIDERED AS GUESTS.
     ****************************************************** */

    const currentAccessLevel = (() => {
      if (member) {
        if (!isStaffSelection || purchased) {
          return FULL_MODE;
        }
        return SS_GUEST_MODE;
      }
      if (purchased) {
        return PPS_PURCHASED;
      }
      if (isStaffSelection) {
        return SS_GUEST_MODE;
      }
      return REGULAR_GUEST_MODE;
    })();

    setAccessMode(currentAccessLevel);
    setMember(memberStatus);
  }, [allowedStreamIds, item, member, loaded, user]);

  useEffect(() => {
    if (
      !loadedStreamIds ||
      !accessMode ||
      !item ||
      videos.loading ||
      videos.itemUUID === item.UUID ||
      (!reCaptchaElement && enableReCaptchaDownload)
    ) {
      return () => {};
    }

    setVideos({
      loading: true,
      loaded: false,
      itemUUID: item.UUID,
      videos: [],
    });

    loadVideoUrls({
      accessMode,
      allowedStreamIds,
      item,
      token,
      contentCdnUrl,
      reCaptchaTokenRef: reCaptchaElement,
    }).then((mp4) => {
      setVideos({
        itemUUID: item.UUID,
        loading: false,
        loaded: true,
        videos: mp4,
      });
    });
    return () => {};
  }, [
    reCaptchaElement,
    enableReCaptchaDownload,
    videos.loading,
    videos.itemUUID,
    setVideos,
    accessMode,
    allowedStreamIds,
    loadedStreamIds,
    item,
    token,
    contentCdnUrl,
  ]);

  useEffect(() => {
    if (loadedStreamIds && item && accessMode) {
      eventTrigger({ name: item.name });
    }
  }, [item, loadedStreamIds, accessMode]);

  useEffect(() => {
    if (item && item.siteUUID) {
      const found = sites.find((s) => s.UUID === item.siteUUID);
      setSite(found);
    }
  }, [item, sites]);

  const onUserToggleFavor = useCallback(
    (UUID) => {
      const favorParams = {
        objectUUID: UUID,
        objectType: 'MOVIE',
      };

      if (!favorites.includes(UUID)) {
        dispatch(addFavorFn(favorParams));
        setSelfFavorite(true);
      } else {
        dispatch(removeFavorFn(favorParams));
        setSelfFavorite(false);
      }
    },
    [setSelfFavorite, dispatch, favorites],
  );

  const onToggleFavor = useMemo(
    () => (user ? () => item && onUserToggleFavor(item.UUID) : () => redirectToJoin(generateJoinUrl, 'favorite-movie')),
    [user, item, onUserToggleFavor, generateJoinUrl],
  );

  const onRate = useCallback(
    (rating, UUID) => {
      const rateParams = {
        objectUUID: UUID,
        objectType: 'MOVIE',
        rating,
      };

      // prevent resubmitting the same rating
      if (ratings[UUID] !== null && rating === ratings[UUID]) {
        return false;
      }
      dispatch(rateItemFn(rateParams));
      return true;
    },
    [ratings, dispatch],
  );

  const hasGalleryPermissions = useMemo(() => !!(accessMode && loadedStreamIds && allowedStreamIds.length > 0), [
    accessMode,
    loadedStreamIds,
    allowedStreamIds,
  ]);

  const renderRelatedMovies = useCallback(() => {
    if (!item?.relatedMovies?.length) return null;

    return (
      <RelatedMovies
        movies={item.relatedMovies.map((relatedMovie) => ({
          title: relatedMovie.name,
          text: relatedMovie.description,
          path: relatedMovie.path,
          active: relatedMovie.UUID === item.media.galleryUUID,
          imgSrc: `${contentCdnUrl}/${item.siteUUID}${relatedMovie.imagePath}`,
          UUID: relatedMovie.UUID,
        }))}
      />
    );
  }, [contentCdnUrl, item]);

  const finalFavoritesCount = useMemo(() => (item ? (item.favoriteCount || 0) + (selfFavorite ? 1 : 0) : 0), [
    item,
    selfFavorite,
  ]);

  const renderMovieContent = () => {
    if (!item) return null;

    const { UUID } = item;

    const [movieCover, isSafe] = getMovieCover({ showSfwSite, item, contentCdnUrl });

    const coverImg = (
      <BlurrableImage className="cover-image" src={movieCover} alt={item.name} $isBlurred={showSfwSite && !isSafe} />
    );

    const hasTeaser = !!item.files.teasers.length;
    const accessWithToken = !!token;
    const showLoader = (hasGalleryPermissions && !accessWithToken && !loaded) || (hasTeaser && !loaded);

    const favorited = accessMode === FULL_MODE && !loading && favorites.includes(item.UUID);
    const showSignup =
      !loading && item?.files?.sizes?.relatedPhotos && !item.files.teasers.length && allowedStreamIds.length === 0;

    const showPlayer =
      (hasGalleryPermissions || hasTeaser) && // show if viewer has permissions (this is true is accesing with a valid token)
      (loaded || accessWithToken); // show if video has loaded ( we dont need video to load when accessing with a token)

    const movieRating = parseFloat(item.ratingAverage.toFixed(ratingsScale));

    return (
      <>
        <div className={allowedStreamIds.length === 0 ? '' : 'play-movie-button'}>
          {/* class of play movie button will override play button with play movie text */}
          {!isLightsOn && <div className="dimmed dimming-background" />}
          <div className="row">
            {showLoader ? (
              <LoaderWrapper reloadFunction={loadData} defaultHeight />
            ) : (
              <div id="player" className={`player ${!isLightsOn ? 'dimmed' : ''}`}>
                {loading && coverImg}
                {showSignup && <JoinButton campaign="movie-cover">{coverImg}</JoinButton>}

                <MoviePlayerContainer
                  showPlayer={showPlayer}
                  accessMode={accessMode}
                  contentCdnUrl={contentCdnUrl}
                  item={item}
                  token={token}
                  videos={videos}
                />
              </div>
            )}

            <div className="container information">
              <div className="col-md-6 col-xs-12 no-pad hidden-sm hidden-xs">
                {!user || (favoriteInfo.loaded && ratingInfo.loaded) ? (
                  <>
                    <Badges className="pull-left stats">
                      <Badge
                        icon={<FontAwesomeIcon icon={favorited ? unfavoriteIcon : favoriteIcon} />}
                        className={cx('black-text movie-badge-button', { favorited })}
                        id="favor-btn"
                        text={finalFavoritesCount}
                        onClick={onToggleFavor}
                      />
                      {!item.isStaffSelection && (
                        <Badge
                          className="movie-badge-button"
                          icon={<FontAwesomeIcon icon={faComments} />}
                          text={comments[UUID] ? comments[UUID].commentsCount : 0}
                          tooltip="Comments"
                          onClick={() => scrollTo('comment-section')}
                        />
                      )}
                    </Badges>
                    <div className="hidden-sm hidden-xs pull-left top-rating">
                      {accessMode === FULL_MODE && (
                        <RatingSlider
                          className="rating-slider"
                          userRating={ratings[item.UUID]}
                          submitRating={(rating) =>
                            user && accessMode === FULL_MODE
                              ? onRate(rating, item.UUID)
                              : redirectToJoin(generateJoinUrl, 'rate-movie')
                          }
                          disabled={!user}
                        />
                      )}
                    </div>
                    <span className="movie-avg-rating">{`Rating: ${movieRating}`}</span>
                  </>
                ) : (
                  <MovieInformationPlaceholder width={100} height={30} />
                )}
              </div>
              <MovieActions
                accessMode={accessMode}
                accessWithToken={accessWithToken}
                contentCdnUrl={contentCdnUrl}
                isLightsOn={isLightsOn}
                setShowUpgradeModal={setShowUpgradeModal}
              />
            </div>

            <MovieActivityPanel
              ratingAverage={parseFloat(item.ratingAverage.toFixed(ratingsScale))}
              objectUUID={item.UUID}
              objectType={item.type}
              ratings={item.ratingCount}
              commentCount={item.comments.total}
              favoriteCount={finalFavoritesCount}
              isFavorited={favorites.includes(item.UUID)}
              onToggleFavor={onToggleFavor}
              views={item.views}
              itemUrl={item.path}
              fullMode={accessMode === FULL_MODE}
            />

            {isPromoInlineBannerActive && <PromoInlineBanner />}
            {isPromoCrossBannerActive && site && <PromoCrossBanner promoSite={site} promoGalleryUUID={item.UUID} />}
            {!isPromoInlineBannerActive && !isPromoCrossBannerActive && (
              <PartnerInlineBanner
                setPromoPhotographer={item?.photographers[0]?.UUID}
                categories={item?.categories}
                site={site}
                setType="movie"
              />
            )}

            {showPromoBanner && site && (
              <PromoBanner promoSite={site} promoGalleryUUID={item.UUID} onClose={() => setShowPromoBanner(false)} />
            )}
          </div>
          {(accessMode === FULL_MODE || accessMode === PPS_PURCHASED) && (
            <RelatedGalleries
              relatedGallery={item.relatedGallery}
              hasGalleryPermissions={hasGalleryPermissions}
              siteUUID={item.siteUUID}
              contentCdnUrl={contentCdnUrl}
              galleryPath={item.path}
              token={token}
            />
          )}

          <MovieDetails item={item} accessMode={accessMode} hasGalleryPermissions={hasGalleryPermissions} />

          {(accessMode === REGULAR_GUEST_MODE || accessMode === SS_GUEST_MODE) && (
            <RelatedGalleries
              relatedGallery={item.relatedGallery}
              hasGalleryPermissions={hasGalleryPermissions}
              siteUUID={item.siteUUID}
              contentCdnUrl={contentCdnUrl}
              galleryPath={item.path}
              token={token}
            />
          )}
          <div className="wrap">{renderRelatedMovies()}</div>

          <MovieCams />

          {!item.isStaffSelection && (
            <CommentSection
              objectUUID={item.UUID}
              parentItem={item}
              parentItemType="movie"
              {...(accessMode === FULL_MODE
                ? {}
                : {
                    generateTitle: ({ commentCount }) =>
                      `${commentCount} Member${commentCount === 1 ? '' : 's'} ` +
                      `commented on ${item.models.map((model) => model.name).join(' & ')}'s gallery`,
                  })}
            />
          )}
          {!item.isStaffSelection && globalContent && globalContent.length > 0 && (
            <NetworkGalleriesSlider galleries={globalContent} />
          )}
        </div>
        <UpgradeDialog
          show={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          eventTrigger="trial-user-download-film"
        />
      </>
    );
  };

  if (!site || !item) {
    return (
      <Wrapper className="content container">
        <LoaderWrapper reloadFunction={loadData} defaultHeight />
      </Wrapper>
    );
  }

  return (
    <Wrapper className="content container">
      <MovieHelmet site={site} item={item} contentCdnUrl={contentCdnUrl} siteSubdomain={siteSubdomain} />
      <img src={`${pixelApiUrl}/${item.siteUUID}/MOVIE/${item.UUID}`} style={{ display: 'none' }} alt="pixel" />

      {enableReCaptchaDownload && (
        <ReCAPTCHA ref={reCaptchaRef} sitekey={config.googleReCaptchaEnterprise.key} size="invisible" />
      )}
      {loading || !videos.loaded || error ? (
        <LoaderWrapper
          defaultHeight
          reloadFunction={loadData}
          loadingStatus={loading}
          reduxErrorStatus={error}
          contentType="models"
        />
      ) : (
        <div>{renderMovieContent()}</div>
      )}
      <RecentCookiesItemInjector
        type={item.type}
        path={item.path}
        name={item.name}
        siteUUID={item.siteUUID}
        thumbnailCoverPath={item.thumbnailCoverPath}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  & .jwplayer {
    position: relative;
    z-index: 900;
    outline: none;

    :hover {
      cursor: pointer;
    }

    .jw-controls-backdrop {
      background: transparent;
    }

    &.jw-state-idle .jw-controls {
      background: transparent;
    }
  }

  & .grecaptcha-badge {
    z-index: 2001;
    width: 70px !important;
    overflow: hidden !important;
    transition: all 0.3s ease !important;
    left: 4px !important;

    :hover {
      width: 256px !important;
    }
  }

  & .lights-text {
    padding-right: 10px;
  }

  & .top-rating {
    margin-left: 20px;
    margin-top: -1px;
  }

  & .information {
    position: relative;
    margin: 20px 0;
    padding: 0 15px;

    @media ${TABLET_LANDSCAPE} {
      margin-top: 30px;
    }

    & > div.no-pad {
      margin-bottom: 0px;
    }

    .movie-badge-button {
      .inner {
        color: ${(props) => props.theme.primary4} !important;
      }
    }

    .movie-avg-rating {
      position: relative;
      display: inline-block;
      left: 25px;
    }
  }

  & .nav-tabs {
    @media (max-width: 767px) {
      display: none;
    }
  }

  & .information .stats {
    @media (max-width: 767px) {
      display: none;
    }
    margin-left: 0;

    li > a {
      cursor: pointer;
      margin-right: 5px;

      i {
        margin-right: 3px;
      }

      :hover,
      :focus {
        text-decoration: none;
      }
    }
  }

  & .dimming-background.dimmed {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 800;
    height: 100%;
    width: 100%;
    background-color: rgba(0, 0, 0, 0.9);
  }

  & .dimming {
    margin-left: 2px;
  }

  & .dimming.dimmed {
    color: #fff;
  }

  & .btn-share {
    text-indent: 5px;
  }

  & .dimmed {
    position: relative;
    z-index: 900;
  }

  & .player {
    display: block;
    outline: none;
    position: relative;
    aspect-ratio: 16 / 9;

    a {
      display: block;
    }
  }

  & .cover-image {
    width: 100%;
    object-fit: contain;
    height: 100%;
    max-height: 790px;
  }

  .favorited svg {
    color: #a80000;
  }

  & .related-gallery {
    max-height: 235px;
    overflow-y: hidden;
    margin-bottom: 20px;

    @media (max-width: 767px) {
      margin: 0 -20px 20px;
    }

    .slick-slide {
      padding: 0 1px;
      box-sizing: border-box;
    }

    .slider-img {
      display: block;
      width: 100%;
      height: 140px;
      overflow: hidden;
    }

    .header {
      display: flex;
      position: relative;
      align-items: center;
      padding: 0 0 10px;

      .title {
        margin: 0;
        @media (max-width: 767px) {
          white-space: nowrap;
          font-size: 1.4rem;
        }
      }

      a {
        right: 10px;
        position: absolute;
        font-size: 1.4rem;
        top: auto;
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }

    .slider {
      padding: 10px;
      border: 1px solid ${(props) => props.theme.primary3};
    }
  }

  & .top-rating .rating-slider {
    top: 6px;

    .mark-wrap {
      top: -15px;
    }

    .mark-text {
      font-size: 0.85rem;
    }

    .star {
      margin-left: -12px;
    }
  }

  .panels-tabs {
    margin: 20px 0;
  }

  .panel-content p a {
    text-decoration: underline;
  }
`;

const MovieActivityPanel = styled(ActivityPanel)`
  width: 100% !important;
  padding: 0 15px;

  @media (min-width: 992px) {
    display: none;
  }

  .favorite-button-wrap {
    .button {
      font-size: 0.9rem;
      padding: 5px 6px;
    }

    .count {
      font-size: 0.85rem;
      width: 30px;
      height: 30px;
      line-height: 30px;
    }

    @media (max-width: 480px) {
      left: 15px;

      .count {
        display: none;
      }
    }
  }

  .rating-slider {
    max-width: 238px;
  }

  .stats {
    display: block;
  }
`;
